import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';
import { MediaQueryDetector } from '../../../../assets/shared/scripts/media-query-detector';
import { mainNavEventEmitterDetails } from '../main-navigation/models/main-navigation.types';

@Component({
  tag: 'wink-flyout',
  styleUrl: 'styles/flyout.default.scss',
  shadow: true,
})
export class Flyout implements ComponentInterface {
  @Element() host!: HTMLWinkFlyoutElement;

  @Prop() flyoutId: number | undefined = undefined;
  @Prop() backButtonLabel: string | undefined = '';

  @State() activeId: number | undefined = undefined;
  @State() backLabel: string | undefined = undefined;
  @State() isFlyoutOpen = false;
  @State() focusableElements: (HTMLElement | null)[] = [];
  @State() focusedElementTracker = 0;

  private closeTimeout: NodeJS.Timeout | null = null;

  @Event() flyoutBackLinkClicked: EventEmitter<{ eventType: 'mouse' | 'keyboard' }> | undefined;

  @Listen('mainNavigationItemInteracted', { target: 'document' })
  mainNavigationItemInteracted(event: CustomEvent<mainNavEventEmitterDetails>) {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.activeId = event.detail.index;
    this.backLabel = event.detail.name;
    this.isFlyoutOpen = this.flyoutId === this.activeId;

    if (event.detail.eventType === 'mouse') {
      return;
    }

    if (this.shouldFetchFocusableElements()) {
      this.getAllFocusableElements();
    } else if (this.shouldFetchMobileFocusableElements()) {
      this.getAllFocusableElements(true);
      requestAnimationFrame(() => {
        this.focusableElements[1]?.focus();
      });
    } else {
      this.resetFocusableElements();
    }

    this.focusOnElement();
  }

  @Listen('mouseover', { target: 'document' })
  handleDocumentMouseOver(event: MouseEvent) {
    if (!this.isFlyoutOpen) return;

    const target = event.target as HTMLElement;
    const globalHeader = target.closest('wink-global-header');

    if (!globalHeader) return;

    const isOverMainNav = target.closest('wink-main-navigation');
    const isOverFlyout = target.closest('wink-flyout') || target.closest('[slot*="flyout"]');

    if (isOverMainNav || isOverFlyout) {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
      return;
    }

    if (!this.closeTimeout && MediaQueryDetector.isFromMdHeader) {
      this.closeTimeout = setTimeout(() => {
        this.resetFlyout();
        this.flyoutBackLinkClicked?.emit({ eventType: 'mouse' });
        this.closeTimeout = null;
      }, 150);
    }
  }

  private shouldFetchFocusableElements() {
    return (
      !this.focusableElements.length &&
      MediaQueryDetector.isFromMdHeader &&
      this.isFlyoutOpen &&
      !this.host.getAttribute('slot')?.includes('mobile')
    );
  }

  private shouldFetchMobileFocusableElements() {
    return (
      !this.focusableElements.length &&
      this.isFlyoutOpen &&
      MediaQueryDetector.isUntilMdHeader &&
      this.host.getAttribute('slot')?.includes('mobile')
    );
  }

  private resetFocusableElements() {
    this.focusableElements = [];
    this.focusedElementTracker = 0;
  }

  private focusOnElement() {
    if (this.focusableElements.length) {
      this.focusableElements[this.focusedElementTracker]?.focus();
    }
  }

  private getAllFocusableElements(mobile = false) {
    const focusableQuery = 'a, button, [tabindex]:not([tabindex="-1"])';
    if (mobile) {
      const menuBackLink = this.host.shadowRoot?.querySelector('wink-link[appearance="menu-back"]');
      if (menuBackLink instanceof HTMLElement) {
        this.focusableElements.push(menuBackLink);
      }
    }

    const navigationLists = this.host.querySelectorAll('wink-navigation-list');
    navigationLists.forEach((list) => {
      const items = list.querySelectorAll('wink-navigation-list-item');
      items.forEach((item) => {
        const focusable = item.shadowRoot?.querySelector(focusableQuery);
        if (focusable instanceof HTMLElement) {
          this.focusableElements.push(focusable);
        }
      });
    });
  }

  @Listen('keydown', {})
  handleKeyDown(e: KeyboardEvent) {
    const { code, shiftKey } = e;
    const { length } = this.focusableElements;

    if (code === 'Escape') {
      this.closeFlyout();
      this.resetFlyout();
    }

    if (code === 'ArrowDown' || code === 'ArrowUp' || code === 'Tab') {
      e.preventDefault();
      this.focusedElementTracker += code === 'ArrowDown' || (code === 'Tab' && !shiftKey) ? 1 : -1;
      this.focusedElementTracker = (this.focusedElementTracker + length) % length;
      (this.focusableElements[this.focusedElementTracker] as HTMLElement).focus();
    }
  }

  disconnectedCallback() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  private resetFlyout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.activeId = undefined;
    this.isFlyoutOpen = false;
    this.focusableElements = [];
    this.focusedElementTracker = 0;
  }

  private closeFlyout() {
    this.resetFlyout();
    this.flyoutBackLinkClicked?.emit({ eventType: 'keyboard' });
  }

  private handleBackKeyDown(e: KeyboardEvent) {
    const { code } = e;
    if (code === 'Enter') {
      this.closeFlyout();
    }
  }

  private handleFlyoutMouseLeave() {
    if (MediaQueryDetector.isUntilMdHeader) {
      return;
    }

    this.resetFlyout();
    this.flyoutBackLinkClicked?.emit({ eventType: 'mouse' });
  }

  render() {
    return (
      <Host
        id={`flyout-${this.flyoutId}`}
        tabindex="-1"
        aria-label={this.flyoutId === this.activeId ? this.backLabel : ' '}
        onMouseLeave={this.handleFlyoutMouseLeave.bind(this)}
        {...{ open: this.isFlyoutOpen }}>
        <div class="container">
          <div class="content">
            <wink-link
              onClick={this.closeFlyout.bind(this)}
              onKeyDown={(e) => this.handleBackKeyDown(e)}
              appearance="menu-back"
              icon="arrow-left"
              size="caption-bold"
              iconPosition="left"
              iconAppearance="default"
              isDisabled={false}
              tabIndex={0}
              href="#">
              <wink-icon slot="icon" appearance="default" icon="arrow-left" size="md" exportparts="icon, icon-arrow-left" />
              {this.backButtonLabel !== '' ? this.backButtonLabel : this.backLabel}
            </wink-link>

            {Array.from({ length: 4 }).map((_, index) => (
              <slot name={`col-${index + 1}`} />
            ))}
          </div>
        </div>
      </Host>
    );
  }
}
